import { useCallback, useState } from 'react';

import { useCreateSupportCaseMutation } from 'generated/graphql-gateway';
import { getErrorDialogForGraphQLCode, parseGraphQLErrorCodes } from 'utils/errors';
import logger from 'utils/logger';

import { CreateCaseState, ICreateSupportCaseInput, IUseKnowledgeForceCaseCreation } from '../types';

export const useKnowledgeForceCaseCreation = (): IUseKnowledgeForceCaseCreation => {
  const [caseReferenceNumber, setCaseReferenceNumber] = useState<string | null | undefined>(null);
  const [createCaseState, setCreateCaseState] = useState<CreateCaseState>(CreateCaseState.DEFAULT);
  const [createCaseError, setCreateCaseError] = useState('');

  const [createSupportCaseMutation] = useCreateSupportCaseMutation();

  const createSupportCase = useCallback(
    async (caseInfo: ICreateSupportCaseInput) => {
      setCreateCaseState(CreateCaseState.LOADING);
      try {
        const { data } = await createSupportCaseMutation({
          variables: {
            caseInfo,
          },
        });
        const caseData = data?.createSupportCase;

        setCaseReferenceNumber(caseData?.caseId);

        setCreateCaseState(CreateCaseState.SUCCESS);
        return caseData?.caseId;
      } catch (error) {
        const [parsedApolloError] = parseGraphQLErrorCodes(error);

        if (parsedApolloError) {
          const mappedErrorMessage = getErrorDialogForGraphQLCode(parsedApolloError);
          if (mappedErrorMessage) {
            setCreateCaseError(mappedErrorMessage.message);
          }
        }

        logger.error(`Failed to create knowledge force support case: ${error.message}`);
        setCreateCaseState(CreateCaseState.ERROR);
        return null;
      }
    },
    [createSupportCaseMutation]
  );

  return {
    caseReferenceNumber,
    createCaseState,
    createSupportCase,
    createCaseError,
  };
};
